import React from "react";
import { motion } from "framer-motion";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    id: "prix",
    q: "Quel est le prix d'un spectacle de drones en France ?",
    a: "Les tarifs drones3x commencent à 8 000 € pour un spectacle de 100 drones d'une durée de 10 à 15 minutes. Comptez 14 500 € pour 200 drones et 35 000 € pour un show épique de 500 drones. Toutes nos prestations incluent la scénarisation sur-mesure, les déclarations DGAC, l'équipe technique et l'assurance.",
  },
  {
    id: "zones",
    q: "Où intervenez-vous en France et en Europe ?",
    a: "Nous réalisons des spectacles de drones partout en France (Paris, Lyon, Marseille, Bordeaux, Toulouse, Nice, Strasbourg, Cannes, Nantes, Lille, Monaco), en Europe francophone (Belgique, Suisse, Luxembourg) ainsi qu'au Maroc. Notre studio est basé à Enghien-les-Bains (95).",
  },
  {
    id: "delai",
    q: "Combien de temps faut-il pour préparer un spectacle de drones ?",
    a: "Comptez en moyenne 8 semaines entre la signature du devis et l'événement. Ce délai couvre la scénarisation artistique, la production de la chorégraphie 3D, les déclarations DGAC, la préparation technique et la répétition sur site. Pour des demandes urgentes, contactez-nous directement au +33 6 66 96 15 35.",
  },
  {
    id: "autorisations",
    q: "Vos spectacles de drones sont-ils autorisés et assurés ?",
    a: "Oui. drones3x est un studio certifié DGAC (scénarios S1 à S3) avec pilotes brevetés et télépilotes professionnels. Toutes nos prestations incluent les déclarations administratives (préfecture, mairie, DGAC) ainsi qu'une assurance responsabilité civile professionnelle.",
  },
  {
    id: "personnalisation",
    q: "Peut-on personnaliser le spectacle (logo, prénoms, date) ?",
    a: "Absolument — chaque spectacle de drones est entièrement sur-mesure. Logos d'entreprise, prénoms des mariés, dates, cœurs, symboles, animations cinématiques 3D et chorégraphies sont conçus en studio puis synchronisés avec votre bande son personnalisée.",
  },
  {
    id: "duree",
    q: "Quelle est la durée d'un spectacle de drones ?",
    a: "La durée standard d'un spectacle de drones drones3x se situe entre 10 et 15 minutes — c'est le format idéal pour maintenir l'émotion sans saturer le public. Pour certains événements grand format (festivals, fêtes de villes), nous proposons des shows plus longs en chaînant plusieurs tableaux.",
  },
  {
    id: "singulier-pluriel",
    q: "Spectacle de drone ou spectacle de drones : quelle différence ?",
    a: "Il n'y a aucune différence technique entre « spectacle de drone » et « spectacle de drones » — les deux désignent la même prestation : une chorégraphie aérienne lumineuse. Le pluriel est plus utilisé car un spectacle mobilise toujours plusieurs dizaines à centaines de drones synchronisés. drones3x réalise des spectacles de drone de 100 à 800 unités partout en France.",
  },
  {
    id: "paris",
    q: "Peut-on organiser un spectacle de drone à Paris et en Île-de-France ?",
    a: "Oui, drones3x est basé à Enghien-les-Bains (95) — à 15 minutes de Paris. Nous réalisons régulièrement des spectacles de drone à Paris, en Île-de-France (92, 93, 94, 95, 77, 78, 91) et dans toute la banlieue. Les autorisations DGAC en zone urbaine dense nécessitent une anticipation particulière que nous prenons en charge.",
  },
  {
    id: "ecologie",
    q: "Un spectacle de drones est-il plus écologique qu'un feu d'artifice ?",
    a: "Oui, nettement. Un spectacle de drones ne produit aucune particule polluante, aucun déchet, et fonctionne sur batteries rechargeables. À l'inverse, un feu d'artifice émet des métaux lourds et laisse des débris. C'est pourquoi de plus en plus de municipalités françaises remplacent leur feu d'artifice par un spectacle de drone pour la fête nationale.",
  },
];

export const FAQ = () => {
  return (
    <section
      id="faq"
      data-testid="faq-root"
      className="relative py-28 md:py-32"
    >
      <div className="mx-auto max-w-5xl px-6">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70">
            <HelpCircle size={12} className="text-[#007AFF]" /> Questions fréquentes
          </div>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
            Tout savoir sur le <span className="italic font-semibold">spectacle de drones</span>.
          </h2>
          <p className="mt-5 text-white/60 leading-relaxed">
            Tarifs, délais, autorisations, personnalisation — les réponses aux questions
            les plus posées par nos clients en France et en Europe.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.7 }}
          className="mt-14 rounded-3xl glass-strong p-3 md:p-4"
        >
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f) => (
              <AccordionItem
                key={f.id}
                value={f.id}
                data-testid={`faq-item-${f.id}`}
                className="border-b border-white/5 last:border-0 px-4 md:px-6"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${f.id}`}
                  className="font-display text-left text-base md:text-lg font-medium text-white hover:no-underline py-6 [&[data-state=open]>svg]:text-[#007AFF]"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-content-${f.id}`}
                  className="text-sm md:text-base text-white/70 leading-relaxed pb-6 pr-8"
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <p className="mt-10 text-center text-sm text-white/50">
          Une autre question ?{" "}
          <a href="#contact" className="text-[#007AFF] hover:underline">
            Écrivez-nous
          </a>{" "}
          ou appelez-nous au{" "}
          <a href="tel:+33666961535" className="text-[#007AFF] hover:underline">
            +33 6 66 96 15 35
          </a>
          .
        </p>
      </div>
    </section>
  );
};

export default FAQ;
